// react
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

// trackers
import * as SocialMediaTracker from "../../PixelTrackers/SocialMediaTracker";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { track as trackGTM } from "../../PixelTrackers/GtmTracker";



// components
import Footer from '../../components/Footer/Footer';
import HeaderController from '../../components/Headers/HeaderController';
import StartConsultationCardContainerDesktop from '../../components/StartConsultationCardContainerDesktop';
import StartConsultationCardContainerMobile from '../../components/StartConsultationCardContainerMobile';
import PrescribingPhysiciansModalTrigger, { DoctorsModalInfo } from './PrescribingPhysiciansModalTrigger';
import LimitationsModalTrigger, { LimitationsModalInfo } from './LimitationsModalTrigger';
import LoadingModal from '../../components/Modals/LoadingModal/LoadingModal';
import ConsultationTypeSelection from './ConsultationTypeSelection';
// css
import "./StartConsultation.css";

// utils
import ServerError from '../../components/ServerError/ServerError';
import * as http from '../../utils/http';
import DarkLoadingModal from "../../components/Modals/DarkLoadingModal/DarkLoadingModal";
import { START_CONSULTATION_PAGE_VIEW_EVENT } from '../../utils/consts';
import { ABTestManager } from '../../utils/ABTestManager';
import { translate } from '../../utils/translator';
import { registerWlExperimentSuperProperties } from '../../utils/wlCarePlusExperiment';
import { markStartConsultationEntry, registerSplitFunnelVariant } from '../../utils/wlFunnelExperiment';
import { getFunnelSplitRedirectUrl, getQuestionnaireMention } from '../../utils/funnelMentionRedirects';

const disallowedCountries: string[] = [];


type Card = {
	step: string | number;
	title: string;
	content: string;
	imgSrc: string;
	imgAlt: string;
};

type HeaderTitles = {
	titleSegments: string[];
	subTitles: string[];
	submitButtonText: string;
};

type Props = any;

const StartConsultation: React.FC<Props> = () => {
	const { country='de', mention, language= 'de' } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [error, setError] = useState<any>(false);
	const [cards, setCards] = useState<Card[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [doctorsModalInfo, setDoctorsModalInfo] = useState<DoctorsModalInfo | null>(null);
	const [headerTitles, setHeaderTitles] = useState<HeaderTitles | null>(null);
	const [limitationsModalInfo, setLimitationsModalInfo] = useState<LimitationsModalInfo | null>(null);
	const [urlCoupon, setUrlCoupon] = useState<string>("");
    const [selectedConsultationType, setSelectedConsultationType] = useState<"video" | "text">("text");
	const [videoConsultationVariable, setVideoConsultationVariable] = useState<number>(-1);
	const [treatmentFees, setTreatmentFees] = useState<{text: number, video: number, cannabisNetwork: number}>({text: 0, video: 0, cannabisNetwork: 0});
	const [isLimitationsModalOpened, setIsLimitationsModalOpened] = useState(false);
	const [isCannabisCatalog, setIsCannabisCatalog] = useState(false);
	const isTextConsultationDisabled = isCannabisCatalog && country?.toUpperCase() === "CH";
	const [customerVerificationRate, setCustomerVerificationRate] = useState(0);

	const isVideoConsultationVariant = videoConsultationVariable === 1 ? true : videoConsultationVariable === 0 ? false :
		new ABTestManager({
			testName: 'video_consultation',
			variants: ['Show', 'Hide'],
			weights: [videoConsultationVariable, 1 - videoConsultationVariable],
		}).getVariant() === 'Show';

	
	let needsCustomerVerification = null;
	if (customerVerificationRate >= 1) needsCustomerVerification = true;
	else if (customerVerificationRate > 0) needsCustomerVerification = new ABTestManager({
		testName: 'customer_verification',
		variants: ['true', 'false'],
		weights: [customerVerificationRate, 1 - customerVerificationRate],
		}).getVariant();

	// get needed data from server
	useEffect(() => {

		sessionStorage.removeItem('express_delivery_address');

		// HL-10959: sticky Care+ A/B arm from CMS entry pages (category / Mounjaro / Wegovy)
		registerWlExperimentSuperProperties();

		const couponCode = window.eventsTracking.getData("cpn");

		if(couponCode){
			setUrlCoupon(couponCode);
		}

		http
			.getFromServer(`start-consultation/${mention}/${country}/${language}?${searchParams.toString()}`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				// UK WL1 vs WL2 split: the WL2 share goes to weight_loss_2. Keep loading true so SC chrome
				// does not flash before the replace lands.
				const splitUrl = getFunnelSplitRedirectUrl(response.data.funnelSplit, mention, window.location);
				registerSplitFunnelVariant(response.data.funnelSplit, splitUrl);
				if (splitUrl) {
					window.location.replace(splitUrl);
					return;
				}
				// Safety net: categories with skip_start_consultation never stay on SC.
				if (response.data.skipStartConsultation) {
					const qs = searchParams.toString();
					const questionnaire = {
						// DE weight_loss_2 opens the weight_loss_3 questionnaire.
						pathname: `/questionnaire/${country}/${language}/${getQuestionnaireMention(country, mention)}`,
						search: qs ? `?${qs}` : "",
					};
					navigate(
						questionnaire,
						// replace drops SC from history so in-app and browser Back
						// both skip this page (no redirect loop).
						{ replace: true }
					);
					// Keep loading true until unmount so SC chrome does not flash.
					return;
				}

				setCards(response.data.cards);
				setDoctorsModalInfo(response.data.doctorsModalInfo);
				setHeaderTitles(response.data.headerTitles);
				setLimitationsModalInfo(response.data.limitationsModalInfo);
				setVideoConsultationVariable(response.data.videoConsultationVariable);
				setTreatmentFees(response.data.treatmentFees);
				setIsCannabisCatalog(response.data.isCannabisCatalog);
				setCustomerVerificationRate(response.data.customerVerificationRate);
				if (response.data.isCannabisCatalog && country?.toUpperCase() === "CH") {
					setSelectedConsultationType("video");
				}
				SocialMediaTracker.track("pv", START_CONSULTATION_PAGE_VIEW_EVENT, {searchParamas: Object.fromEntries(searchParams.entries())});
				// HL-12122: first page of the WL funnel (DE and UK weight_loss_2) → entry_route start_consultation.
				if (["de", "gb"].includes(country.toLowerCase()) && mention?.toLowerCase() === "weight_loss_2") {
					markStartConsultationEntry();
				}

				// Track page view in GTM
				trackGTM("custom", "ga4_auto_event", {
					'event_name': 'medical_questions_before_you_start',
					'funnel_step': 'funnel_Step_1',
					'location': 1,
					'subcategory': mention
					// category will be auto-added by GtmTracker based on subcategory
				});
				setIsLoading(false);
			})
			.catch((error) => {
				window.clientLogger.error('Error in StartConsultation: ', error);
				setError(error?.response?.status || 500);
				setIsLoading(false);
			});
	}, []);

	useEffect(() => {
		if (videoConsultationVariable >= 0) {
			MainTracker.track("custom", "view start consultation with video", {"View video": isVideoConsultationVariant});
		}
	}, [videoConsultationVariable]);
	

	if (error) {
		return <ServerError status={error} />;
	}

	if (isLoading) {
		return <LoadingModal />;
	}

	return (
		<div
			id="start-consultation-page-wrapper"
				className={`min-h-screen pb-[100px] relative
				${isVideoConsultationVariant && `
					bg-[#F5F7F5]
					grid
					md:grid-cols-[minmax(30px,1fr)_minmax(0,1200px)_minmax(30px,1fr)]
					grid-cols-[minmax(20px,1fr)_minmax(0,1200px)_minmax(20px,1fr)]
				`}
			`} 
		>
			{/* Header */}
			<div 
				className={`
					border-b-[1px] 
					[border-bottom-style:solid] 
					border-[#eef1f6]
					${isVideoConsultationVariant && `
						col-span-3
					`}
				`}
			>				
				<HeaderController 
					showBackButton={false} 
					stage="StartConsultation" 
					country={country!} 
					showSteps={false} 
				/>
			</div>

			{/* Main content */}
			<div 
			 	id="start-consultation-page-container" 
				className={`
				 	overflow-x-hidden 
					flex 
					flex-col
					justify-center 
					items-center  
					pt-4
					gap-10
					${isVideoConsultationVariant && `
						col-start-2
					`}
				`}
			>
				{isLoading && <DarkLoadingModal />}

				<div 
				  	id="start-consultation-page" 
					className={`
						w-full 
						flex 
						flex-col 
						lg:w-[1200px]
						items-center 
						pb-0 
						px-4
						${isVideoConsultationVariant && `
							!block
							!lg:w-full
							!px-0
							lg:w-auto
						`}
					`}
				>
					{/* Header  */}
				<div 
					id="start-consulation-header" 
					className={` 
						flex 
						flex-col 
						items-center 
						justify-center 
						text-[#0d2c54] 
						font-poppins 
						${isVideoConsultationVariant && `
							text-[#000000] 
							!block
						`}
					`}
				>
						{/* Main title of the page */}
						<h1 
						 	id="start-consultation-header-title" 
							className={`
							${isVideoConsultationVariant 
								? "font-medium md:text-[40px] text-[28px] md:leading-[1] leading-[1.2] mb-4 w-full" 
								: "md:text-[32px] text-[24px] mb-[10px] text-center text-[#08080A]"
							}
						`}
						>
							{headerTitles?.titleSegments.map((segment, index) => 
							 	<span 
									className={ isVideoConsultationVariant && index === 0 ? "block leading-[1]" : isVideoConsultationVariant && index === 2 ? "bg-[linear-gradient(to_right,#10947D,#91BC28)] bg-clip-text text-transparent leading-[1]" : "leading-[1]"}
									key={index}
								>
									{/* add a space between the segments */}
									{segment}{index < headerTitles.titleSegments.length - 1 ? ' ' : ''}
								</span>)
							}
						</h1>
						
						<h3 id="start-consultation-header-description" 
							className={`
										block
										lg:mb-4
									${isVideoConsultationVariant
										? "text-[#000000] text-[18px]" 
										: "text-[1rem] mb-0 font-normal text-center"
									}
								`}
					>
							{/* Subtitles */}
							{headerTitles?.subTitles?.map((subTitle,index) => (
								<span 
									key={index} 
									className={`
										text-[#616975] 
										font-normal
										font-roboto
										text-[18px]
										leading-[1.5]
										start-consultation-header-subtitle
										${!isVideoConsultationVariant && index !== 0 && "hidden"}
									`}
								>
									{subTitle}
								</span>
							))}
						</h3>
					</div>
					
					<main 
						id="main-container" 
						className="
						 	w-full 
							flex 
							flex-col 
							justify-center 
							items-center
						"
					>
					{isVideoConsultationVariant 
						? 	<ConsultationTypeSelection
								language={language}
								country={country}
								selectedConsultationType={selectedConsultationType}
								setSelectedConsultationType={setSelectedConsultationType}
								treatmentFees={treatmentFees}
								isTextDisabled={isTextConsultationDisabled}
								mention={mention}
							/>
							:   
								<>
									{/* Desktop Layout */}
									<div className="hidden lg:block">
										<StartConsultationCardContainerDesktop cards={cards} />
									</div>
									
									{/* Mobile Layout */}
									<div className="block lg:hidden">
										<StartConsultationCardContainerMobile cards={cards} />
									</div>
								</>
						}
					</main>
				</div>
			</div>

			<div 
			 	id="start-consultation-button" 
				className={`
				 	w-full 
					flex 
					items-center 
					justify-center 
					sticky
					bottom-0 
					right-0
					z-10
					${isVideoConsultationVariant ?
						`
							col-span-3
							bg-transparent
							pb-8
							pt-1
							mt-7
						`
					:
						`
							bg-white
							p-4
						`
					}
				`}
			>
				<div className="flex flex-col items-center w-full">
					<Link
						id="start-consultation-submit-button"
						className={`font-poppins max-w-[350px] w-full p-5 text-white text-center transition-all duration-300 mx-auto block rounded-[12px] bg-[linear-gradient(to_right,var(--primary-lightgreen),#91BC28)] opacity-100
							${disallowedCountries.includes(country)
								? "opacity-50 cursor-not-allowed pointer-events-none bg-gray-300 text-gray-400 hover:opacity-50 hover:underline-none"
								: "cursor-pointer hover:underline hover:decoration-[#00B086] hover:opacity-90"
							}`}
						onClick={() => {
							if (disallowedCountries.includes(country)) {
								return;
							}
							if (selectedConsultationType === "video") {
								MainTracker.track("custom", "started video consultation flow");
							}
							if(isCannabisCatalog){
								// Track user click breakdown event
								MainTracker.track("custom", "user click breakdown", {
									"page": "Start Consultation",
									"Click on delivery options": isLimitationsModalOpened,
									"Click on Medication tooltip": false,
									"Click on Service Fee tooltip": false,
									"Click on Treatment Fee tooltip": false,
									"Click expand product (show/hide)": false,
									"Click on Partner Pharmacies pop-up": false,
									"Click on pharmacy details (expand)": false,
									"Click on terms": false,
									"Click on doctor details tooltip": false,
									"Click on product pharmacy tooltip": false
								});
							}
						}}
						to={!disallowedCountries.includes(country) 
							? `/questionnaire/${country}/${language}/${getQuestionnaireMention(country, mention)}?${searchParams.toString()}&cpn=${urlCoupon}&ct=${isTextConsultationDisabled ? "video" : selectedConsultationType}&cv=${needsCustomerVerification}` 
							: "#"
						}
						tabIndex={disallowedCountries.includes(country) ? -1 : 0}
						aria-disabled={disallowedCountries.includes(country)}
					>
				 		{isVideoConsultationVariant ? translate(language, "start_consultation", "start_consultation_button_text_video_variant") : headerTitles?.submitButtonText}
					</Link>
				</div>
			</div>

			<div 
				id="start-consultation-modal-container" 
				className={`
					flex 
					justify-center 
					items-center
					flex-row 
					gap-6 
					md:gap-6 
					text-xs 
					font-poppins
					${isVideoConsultationVariant && `
						col-span-3
					`}
				`}
			>
				{doctorsModalInfo && <PrescribingPhysiciansModalTrigger doctorsModalInfo={doctorsModalInfo} />}
				{limitationsModalInfo && <LimitationsModalTrigger limitationsInfo={limitationsModalInfo} onOpen={() => setIsLimitationsModalOpened(true)} />}
			</div>

			{/* Footer */}
			<Footer 
				page="questionnaire" 
				layoutOptions={`absolute bottom-0 left-0 ${isVideoConsultationVariant ? "col-span-3 py-4" : ""}`} 
			/>
		</div>
	);
};

export default StartConsultation;
